import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Button } from "./Button";
import { SearchIcon, XIcon } from "./icons";
import styles from "./SearchBox.module.css";

// Below 480 px the box between brand and actions is too narrow to read: at
// 375 px it rendered about 90 px wide and even "Search" showed as "Sear". There
// the search is an icon button that opens a full-width field over the header.
const NARROW = "(max-width: 479px)";

function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(NARROW).matches ?? false);
  useEffect(() => {
    const query = window.matchMedia?.(NARROW);
    if (!query) return;
    const onChange = () => setNarrow(query.matches);
    onChange();
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return narrow;
}

/**
 * The header search box (prototype .search-wrap): "/" focuses it from
 * anywhere in the app (unless another field already has focus), Enter
 * navigates to the results page. Stays in sync with ?q= while already
 * viewing results. Below 480 px it is an icon button; a tap (or "/") opens the
 * field over the header with focus in it, and Escape or the close button
 * closes it.
 */
export function SearchBox() {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const narrow = useNarrow();
  const [open, setOpen] = useState(false);
  const openBtnRef = useRef<HTMLButtonElement>(null);
  const showField = !narrow || open;

  useEffect(() => {
    if (location.pathname !== "/search") return;
    setValue(new URLSearchParams(location.search).get("q") ?? "");
  }, [location.pathname, location.search]);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "/") return;
      const active = document.activeElement;
      const typing =
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable);
      if (typing) return;
      e.preventDefault();
      setOpen(true);
      inputRef.current?.focus();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // The field mounts when the phone overlay opens, so focus it then.
  useEffect(() => {
    if (narrow && open) inputRef.current?.focus();
  }, [narrow, open]);

  function close() {
    setOpen(false);
    // Back to the button that opened it, once it has mounted again.
    setTimeout(() => openBtnRef.current?.focus());
  }

  function submit() {
    const q = value.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    if (narrow) close();
  }

  if (!showField) {
    return (
      <Button
        ref={openBtnRef}
        className={styles.openBtn}
        aria-label="Search OpsHub"
        aria-expanded={false}
        onClick={() => setOpen(true)}
      >
        <SearchIcon />
      </Button>
    );
  }

  return (
    <div
      className={narrow ? `${styles.wrap} ${styles.overlay}` : styles.wrap}
      // On the wrapper, so Escape also closes from the close button.
      onKeyDown={(e) => {
        if (e.key === "Escape" && narrow) close();
      }}
    >
      <SearchIcon className={styles.icon} />
      <input
        ref={inputRef}
        type="search"
        name="q"
        className={styles.input}
        placeholder="Search OpsHub…"
        aria-label="Search OpsHub"
        autoComplete="off"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
        }}
      />
      {narrow ? (
        <button type="button" className={styles.closeBtn} aria-label="Close search" onClick={close}>
          <XIcon />
        </button>
      ) : (
        <span className={styles.hint} aria-hidden="true">
          /
        </span>
      )}
    </div>
  );
}
